import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { IntranetSeguridadService } from '../../../../core/services/intranet-seguridad.service';
import { AlertaService } from '../../../../core/services/alerta.service';

interface DashboardSerie {
    periodo: string;
    cantidad: number;
}

interface DashboardSerieMonto {
    periodo: string;
    monto: number;
}

interface DashboardEstado {
    estado: number;
    cantidad: number;
}

interface DashboardResumen {
    solicitudesTotal: number;
    solicitudesFinalizadas: number;
    porcentajeFinalizadas: number;
    usuariosActivos: number;
    vehiculosVisibles: number;
    valoracionesTotal: number;
    montoRecargas: number;
    montoPagos: number;
    promocionesVigentes: number;
    solicitudesPorEstado: DashboardEstado[];
    solicitudesPorMes: DashboardSerie[];
    usuariosPorMes: DashboardSerie[];
    recargasPorMes: DashboardSerieMonto[];
    pagosPorMes: DashboardSerieMonto[];
}

@Component({
    selector: 'app-dashboard',
    templateUrl:'./dashboard.component.html',
    styleUrls: ['./dashboard.component.scss', './dashboard-finance.component.scss'],
    imports: [CommonModule]
})

export class DashboardComponent implements OnInit {
    cargando = true;
    resumen: DashboardResumen = {
        solicitudesTotal: 0,
        solicitudesFinalizadas: 0,
        porcentajeFinalizadas: 0,
        usuariosActivos: 0,
        vehiculosVisibles: 0,
        valoracionesTotal: 0,
        montoRecargas: 0,
        montoPagos: 0,
        promocionesVigentes: 0,
        solicitudesPorEstado: [],
        solicitudesPorMes: [],
        usuariosPorMes: [],
        recargasPorMes: [],
        pagosPorMes: []
    };
    solicitudesMensuales: DashboardSerie[] = [];
    usuariosMensuales: DashboardSerie[] = [];
    recargasMensuales: DashboardSerieMonto[] = [];
    pagosMensuales: DashboardSerieMonto[] = [];

    constructor(
        private readonly seguridad: IntranetSeguridadService,
        private readonly alerta: AlertaService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.cargarResumen();
    }

    cargarResumen(): void {
        this.cargando = true;
        this.seguridad.post<DashboardResumen>('dashboard/resumen').subscribe({
            next: (respuesta) => {
                this.cargando = false;
                if (!respuesta.isSuccess || !respuesta.value) {
                    this.alerta.openAlerta('Dashboard', respuesta.message || 'No se pudo cargar el resumen', 'error');
                    return;
                }
                this.resumen = respuesta.value;
                this.solicitudesMensuales = this.normalizarMeses(respuesta.value.solicitudesPorMes);
                this.usuariosMensuales = this.normalizarMeses(respuesta.value.usuariosPorMes);
                this.recargasMensuales = this.normalizarMesesMonto(respuesta.value.recargasPorMes);
                this.pagosMensuales = this.normalizarMesesMonto(respuesta.value.pagosPorMes);
                this.cdr.detectChanges();
            },
            error: () => {
                this.cargando = false;
                this.alerta.openAlerta('Dashboard', 'No se pudo cargar el resumen del aplicativo', 'error');
                this.cdr.detectChanges();
            }
        });
    }

    porcentajeEstado(estado: DashboardEstado): number {
        return this.resumen.solicitudesTotal ? Math.round((Number(estado.cantidad) / this.resumen.solicitudesTotal) * 100) : 0;
    }

    porcentajeBarra(serie: DashboardSerie[], cantidad: number): number {
        const maximo = Math.max(...serie.map((item) => Number(item.cantidad)), 1);
        return Math.max((Number(cantidad) / maximo) * 100, cantidad ? 8 : 0);
    }

    porcentajeBarraMonto(cantidad: number): number {
        const montos = [...this.recargasMensuales, ...this.pagosMensuales].map((item) => Number(item.monto));
        const maximo = Math.max(...montos, 1);
        return Math.max((Number(cantidad) / maximo) * 100, cantidad ? 8 : 0);
    }

    formatearMonto(monto: number): string {
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 0 }).format(Number(monto ?? 0));
    }

    montoPagoMensual(indice: number): number {
        return Number(this.pagosMensuales[indice]?.monto || 0);
    }

    etiquetaEstado(estado: number): string {
        return ({ 1: 'Enviadas', 2: 'Rechazadas', 3: 'Enviadas a conductor', 4: 'Aceptadas', 5: 'Finalizadas', 6: 'Canceladas', 7: 'Rechazadas por conductor' } as Record<number, string>)[Number(estado)] ?? 'Sin estado';
    }

    claseEstado(estado: number): string {
        return `dashboard-state--${Number(estado)}`;
    }

    private normalizarMeses(datos: DashboardSerie[]): DashboardSerie[] {
        const meses: DashboardSerie[] = [];
        const fechaActual = new Date();
        for (let indice = 5; indice >= 0; indice--) {
            const fecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() - indice, 1);
            const periodo = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            const encontrado = datos.find((item) => item.periodo === periodo);
            meses.push({
                periodo: new Intl.DateTimeFormat('es-PE', { month: 'short' }).format(fecha).replace('.', ''),
                cantidad: Number(encontrado?.cantidad ?? 0)
            });
        }
        return meses;
    }

    private normalizarMesesMonto(datos: DashboardSerieMonto[]): DashboardSerieMonto[] {
        const meses: DashboardSerieMonto[] = [];
        const fechaActual = new Date();
        for (let indice = 5; indice >= 0; indice--) {
            const fecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() - indice, 1);
            const periodo = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            const encontrado = (datos ?? []).find((item) => item.periodo === periodo);
            meses.push({
                periodo: new Intl.DateTimeFormat('es-PE', { month: 'short' }).format(fecha).replace('.', ''),
                monto: Number(encontrado?.monto ?? 0)
            });
        }
        return meses;
    }

}
